import React, { useState, useCallback } from 'react';
import { Alert, Typography, Button, Box } from '@mui/material';
import AdminLayout from '../../components/AdminLayout';
import AdminTable from '../../components/AdminTable';
import apiClient from '../../services/apiClient';

function formatDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// 999.18482 — DELETE /api/admin/sessions/:id requires step-up (403 STEP_UP_REQUIRED).
function terminateRefusalMessage(err) {
  if (err.data && err.data.error === 'STEP_UP_REQUIRED') {
    return 'Terminating a session needs a recent two-factor check. Sign out and back in with your authenticator code, then try again.';
  }
  return err.message;
}

export default function ActiveSessions() {
  const [refreshKey, setRefreshKey] = useState(0);
  const [error, setError] = useState(null);

  const fetchSessions = useCallback(async (params) => {
    const qs = new URLSearchParams({
      limit: String(params.limit),
      offset: String(params.offset),
      sort_field: params.sort_field,
      sort_dir: params.sort_dir,
    });
    if (params.search) qs.set('search', params.search);

    const data = await apiClient.get(`/api/admin/sessions/active?${qs}`);
    return { rows: data.sessions || [], total: data.total || 0 };
  }, []);

  async function terminateSession(id) {
    if (!window.confirm('Terminate this session?')) return;
    setError(null);
    try {
      await apiClient.del(`/api/admin/sessions/${id}`);
    } catch (err) {
      setError(terminateRefusalMessage(err));
    }
    setRefreshKey(k => k + 1);
  }

  async function bulkTerminate(ids) {
    if (!window.confirm(`Terminate ${ids.length} session(s)?`)) return;
    setError(null);
    for (const id of ids) {
      try {
        await apiClient.del(`/api/admin/sessions/${id}`);
      } catch (err) {
        // Same as 999.18468 bulk status: stop at the first refusal and report it.
        setError(terminateRefusalMessage(err));
        break;
      }
    }
    setRefreshKey(k => k + 1);
  }

  const columns = [
    {
      id: 'user_email', label: 'User', minWidth: 180,
      render: (row) => (
        <Box>
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
            {row.user_name || row.user_email}
          </Typography>
          {row.user_name && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              {row.user_email}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: 'device_info', label: 'Device', minWidth: 120, sortable: false,
      render: (row) => row.device_info || 'Unknown',
    },
    { id: 'ip_address', label: 'IP Address', minWidth: 120 },
    {
      id: 'created_at', label: 'Started', minWidth: 130,
      render: (row) => formatDateTime(row.created_at),
    },
    {
      id: 'last_activity', label: 'Last Activity', minWidth: 130,
      render: (row) => formatDateTime(row.last_activity),
    },
  ];

  return (
    <AdminLayout>
      <Typography variant="h4" sx={{ mb: 3, fontFamily: "'EB Garamond', serif" }}>
        Active Sessions
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <AdminTable
        tableId="admin_sessions"
        columns={columns}
        fetchData={fetchSessions}
        selectable
        bulkActions={[
          { label: 'Terminate Selected', color: 'error', onClick: bulkTerminate },
        ]}
        actions={(row) => (
          <Button
            size="small" variant="outlined" color="error"
            onClick={() => terminateSession(row.id)}
            sx={{ fontSize: '0.65rem', py: 0.25, px: 1, minWidth: 0, textTransform: 'none' }}
          >
            Terminate
          </Button>
        )}
        refreshKey={refreshKey}
        defaultSort={{ field: 'last_activity', dir: 'desc' }}
      />
    </AdminLayout>
  );
}
